import { useLocalSearchParams } from "expo-router";
import { ActivityIndicator, Text, View } from "react-native";
import { usePublicEvent } from "@/features/events/api/events";
import { usePublicEventParticipants } from "@/features/events/api/event-participants";
import { useRequireAuth } from "@/features/auth/hooks/use-require-auth";
import { EventDetailView } from "@/features/events/components/event-detail-view";

/**
 * Public (logged-out) event detail. Reads from the anon endpoints and carries
 * no mutation code — every engagement action routes through `useRequireAuth`,
 * which sends the visitor to login and returns them here afterwards.
 * Rendered by `event-detail.tsx` only when `isLoggedIn` is false.
 */
export default function PublicEventDetail() {
  const { eventId } = useLocalSearchParams<{ eventId: string }>();
  const requireAuth = useRequireAuth();

  const { data: event, isLoading, error } = usePublicEvent(eventId);
  const { data: participantsPreview, isLoading: isLoadingParticipants } =
    usePublicEventParticipants(eventId);

  if (isLoading) {
    return (
      <View className="bg-background flex-1 items-center justify-center">
        <ActivityIndicator
          size="large"
          className="text-brand"
          accessibilityLabel="Loading event details"
        />
      </View>
    );
  }

  if (error || !event) {
    return (
      <View className="bg-background flex-1 items-center justify-center">
        <Text
          className="text-destructive text-center text-base"
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          Failed to load event
        </Text>
      </View>
    );
  }

  return (
    <EventDetailView
      event={event}
      participants={participantsPreview?.participants}
      totalParticipantsCount={participantsPreview?.totalCount}
      isLoadingParticipants={isLoadingParticipants}
      isUserConfirmed={false}
      isUserInterested={false}
      isInterestPending={false}
      isConfirmPending={false}
      onToggleInterest={() => requireAuth(`/event/${eventId}`, () => {})}
      onPressConfirm={() => requireAuth(`/event/${eventId}`, () => {})}
      onPressParticipants={() =>
        requireAuth(`/event/${eventId}/participants`, () => {})
      }
      onPressLockedCoupon={() => requireAuth(`/event/${eventId}`, () => {})}
    />
  );
}
